import React from 'react'
import { theme } from '../theme'

/**
 * Email a batch report.
 *
 * There is no dedicated mail configuration: the send borrows the SMTP credentials of an active job
 * that already has outbound SMTP set up. That avoids a second place to configure and a second place
 * to get wrong — but it means the action can fail for a reason the operator can fix in seconds
 * ("no enabled job has outbound SMTP configured"), so the backend's error text is surfaced
 * verbatim rather than replaced with "email failed".
 */
export function EmailReport({ batchId }: { batchId: string }) {
  const [open, setOpen] = React.useState(false)
  const [to, setTo] = React.useState('')
  const [csv, setCsv] = React.useState(true)
  const [busy, setBusy] = React.useState(false)
  const [err, setErr] = React.useState<string | null>(null)
  const [sent, setSent] = React.useState<string | null>(null)

  async function send() {
    setBusy(true)
    setErr(null)
    setSent(null)
    try {
      const recipients = to
        .split(/[,;\s]+/)
        .map(s => s.trim())
        .filter(Boolean)
      if (recipients.length === 0) throw new Error('Enter at least one recipient')
      const r = await fetch(`/api/batches/${batchId}/email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipients, include_csv: csv }),
      })
      const body = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(body.detail || `HTTP ${r.status}`)
      // Report what the transport actually said, including partial refusals — aiosmtplib only
      // raises when EVERY recipient is refused, so a partial failure arrives as data and would
      // otherwise be presented as complete success.
      const refused = Object.keys(body.refused || {}).length
      setSent(
        refused
          ? `Sent, but ${refused} recipient(s) were refused: ${JSON.stringify(body.refused)}`
          : `Sent to ${recipients.length} recipient(s) via job "${body.borrowed_job}"`,
      )
      setOpen(false)
      setTo('')
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  if (!open) {
    return (
      <div>
        <button
          onClick={() => setOpen(true)}
          style={{ padding: '0.4rem 0.8rem', fontSize: '0.78rem', background: theme.primary, color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}
        >
          Email report
        </button>
        {sent && (
          <div style={{ fontSize: '0.72rem', color: '#4caf50', marginTop: '0.35rem', maxWidth: '22rem' }}>
            {sent}
          </div>
        )}
        {err && (
          <div style={{ fontSize: '0.72rem', color: '#ef9a9a', marginTop: '0.35rem', maxWidth: '22rem' }}>
            {err}
          </div>
        )}
      </div>
    )
  }

  return (
    <div style={{ background: theme.bgAlt, border: `1px solid ${theme.border}`, borderRadius: 6, padding: '0.7rem', minWidth: '20rem' }}>
      <input
        type="text"
        value={to}
        onChange={e => setTo(e.target.value)}
        placeholder="recipient@example.com, another@example.com"
        style={{ width: '100%', padding: '0.4rem', background: theme.bg, color: theme.text, border: `1px solid ${theme.border}`, borderRadius: 4, fontSize: '0.78rem' }}
      />
      <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.74rem', color: '#9aa0a6', marginTop: '0.5rem' }}>
        <input type="checkbox" checked={csv} onChange={e => setCsv(e.target.checked)} />
        Attach the per-fax CSV
      </label>
      <div style={{ fontSize: '0.7rem', color: '#666', marginTop: '0.4rem' }}>
        Sent as <code>noreply@ec2.ccs.biz</code>, borrowing an active job's SMTP credentials. The
        envelope sender stays that job's authenticated identity, because many relays reject a
        From: domain they did not authenticate.
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.6rem' }}>
        <button
          onClick={send}
          disabled={busy || !to.trim()}
          style={{ padding: '0.4rem 0.9rem', fontSize: '0.78rem', background: to.trim() ? theme.primary : theme.bgElevated, color: to.trim() ? '#fff' : '#666', border: 'none', borderRadius: 4, cursor: busy || !to.trim() ? 'default' : 'pointer', fontWeight: 600 }}
        >
          {busy ? 'Sending…' : 'Send'}
        </button>
        <button
          onClick={() => { setOpen(false); setErr(null) }}
          style={{ padding: '0.4rem 0.7rem', fontSize: '0.78rem', background: 'transparent', color: '#9aa0a6', border: `1px solid ${theme.border}`, borderRadius: 4, cursor: 'pointer' }}
        >
          Cancel
        </button>
      </div>
      {err && (
        <div style={{ fontSize: '0.74rem', color: '#ef9a9a', marginTop: '0.5rem' }}>{err}</div>
      )}
    </div>
  )
}
